import { Head, router } from "@inertiajs/react"
import { RefreshCw01, SearchLg } from "@untitledui/icons"
import { format, formatDistanceToNow } from "date-fns"

import { FilterChip, FilterRow } from "@/components/admin/filter-chip"
import { PageHeader } from "@/components/console/page-header"
import { Button } from "@/components/ui/button"
import { EmptyState } from "@/components/ui/empty-state"
import { Notice } from "@/components/ui/notice"
import { AdminLayout } from "@/layouts/admin-layout"
import {
  adminCallListPath,
  adminCallListsPath,
  refreshAdminCallListPath,
  regenerateAdminCallListPath,
} from "@/routes"

import { AddSchoolModal } from "./_components/add-school-modal"
import { CallListCard } from "./_components/call-list-card"
import type { ListFilters, ShowProps } from "./types"

const formatStamp = (iso: string | null) =>
  iso ? format(new Date(iso), "d MMM yyyy 'at' HH:mm") : "never"

export default function AdminCallListsShow({
  scope,
  entries,
  filters,
  available_filters,
  counts,
  snapshot,
  mirror,
  list,
  school_candidates,
}: ShowProps) {
  const visitWith = (next: Partial<ListFilters>) => {
    const merged = { ...filters, ...next }
    const data: Record<string, string> = {}
    if (merged.scenario) data.scenario = merged.scenario
    if (merged.include_hidden) data.include_hidden = "1"
    router.visit(adminCallListPath(scope.id), {
      data,
      preserveState: true,
      preserveScroll: true,
      only: ["entries", "filters", "counts"],
    })
  }

  const regenerate = () =>
    router.post(
      regenerateAdminCallListPath(scope.id),
      {},
      { preserveScroll: true },
    )
  const refresh = () =>
    router.post(
      refreshAdminCallListPath(scope.id),
      {},
      { preserveScroll: true },
    )

  return (
    <AdminLayout>
      <Head title={`Call list · ${scope.name}`} />
      <div className="mx-auto flex max-w-5xl flex-col gap-6 px-4 lg:px-8">
        <PageHeader
          crumbs={[
            { label: "call list" },
            { label: "lists", href: adminCallListsPath() },
            { label: scope.name },
          ]}
          title={scope.name}
          facts={
            <>
              {scope.label} · {counts.shown} to call today
              {counts.hidden > 0 ? ` · ${counts.hidden} hidden` : ""}
              {counts.skipped_today > 0
                ? ` · ${counts.skipped_today} skipped`
                : ""}
            </>
          }
          actions={
            <>
              <AddSchoolModal
                scopeId={scope.id}
                candidates={school_candidates}
              />
              <Button size="md" color="secondary" onPress={regenerate}>
                Rebuild today's list
              </Button>
              <Button
                size="md"
                color="secondary"
                iconLeading={RefreshCw01}
                onPress={refresh}
                isDisabled={!scope.refreshable}
              >
                Refresh from Salesforce
              </Button>
            </>
          }
        />

        {snapshot.missing ? (
          <Notice tone="error" role="alert">
            Nothing has been refreshed for {scope.label} yet. Refresh from
            Salesforce, then rebuild the list.
          </Notice>
        ) : snapshot.stale ? (
          <Notice tone="warning" role="status">
            Ranked from a snapshot computed {formatStamp(snapshot.computed_at)}.
            The nightly refresh has not run since, so chips may be out of date.
          </Notice>
        ) : null}

        <div className="flex flex-col gap-3">
          <FilterRow label="Scenario">
            <FilterChip
              active={!filters.scenario}
              onClick={() => visitWith({ scenario: null })}
            >
              All
            </FilterChip>
            {available_filters.scenarios.map(({ key, label }) => (
              <FilterChip
                key={key}
                active={filters.scenario === key}
                onClick={() => visitWith({ scenario: key })}
              >
                {label}
              </FilterChip>
            ))}
          </FilterRow>
          <FilterRow label="Show">
            <FilterChip
              active={!filters.include_hidden}
              onClick={() => visitWith({ include_hidden: false })}
            >
              Callable today
            </FilterChip>
            <FilterChip
              active={filters.include_hidden}
              onClick={() => visitWith({ include_hidden: true })}
            >
              Including hidden
            </FilterChip>
          </FilterRow>
        </div>

        {entries.length === 0 ? (
          <EmptyState size="md" className="my-12">
            <EmptyState.Header>
              <EmptyState.FeaturedIcon
                icon={SearchLg}
                color="gray"
                theme="modern"
              />
            </EmptyState.Header>
            <EmptyState.Content>
              <EmptyState.Title>Nobody to call today</EmptyState.Title>
              <EmptyState.Description>
                {counts.hidden > 0
                  ? `${counts.hidden} ${counts.hidden === 1 ? "school is" : "schools are"} hidden by a guard, a snooze or a follow-up.`
                  : "No school in this scope is on today's list."}
              </EmptyState.Description>
            </EmptyState.Content>
            {counts.hidden > 0 && !filters.include_hidden && (
              <EmptyState.Footer>
                <Button
                  size="md"
                  color="secondary"
                  onPress={() => visitWith({ include_hidden: true })}
                >
                  Show hidden
                </Button>
              </EmptyState.Footer>
            )}
          </EmptyState>
        ) : (
          <ol className="flex flex-col gap-3">
            {entries.map((entry) => (
              <li key={entry.entry_id}>
                <CallListCard
                  entry={entry}
                  listId={list.id}
                  scopeId={scope.id}
                />
              </li>
            ))}
          </ol>
        )}

        <p className="text-quaternary text-xs">
          List built{" "}
          {formatDistanceToNow(new Date(list.built_at), { addSuffix: true })} ·
          jobs mirror synced {formatStamp(mirror.synced_at)}
        </p>
      </div>
    </AdminLayout>
  )
}
